<template>
  <div class="motor-page motor-page--customer">
    <header class="motor-header">
      <button type="button" class="back-btn" @click="$router.push({ name: productsRoute })">
        <v-icon size="18" color="#1E4276">mdi-arrow-left</v-icon>
      </button>
      <div>
        <p class="breadcrumb">{{ pageBreadcrumb }}</p>
        <h1 class="title">{{ pageTitle }}</h1>
      </div>
    </header>

    <customer-agent-picker v-if="!guest" />

    <div class="quote-layout">
      <div class="quote-form-column">
        <div class="section-card">
          <div class="section-header">
            <span class="section-number">1</span>
            <h3>Vehicle details</h3>
          </div>
          <p class="step-hint">Tell us about your private vehicle to get live premiums.</p>

          <v-form v-model="formValid">
            <div class="motor-form-fields">
              <div class="form-row">
                <div class="input-group">
                  <label class="input-label">Vehicle make <span class="req">*</span></label>
                  <v-autocomplete
                    v-model="carMake"
                    :items="carMakeItems"
                    :loading="carLoading"
                    :search-input.sync="carMakeSearch"
                    placeholder="Select make"
                    outlined
                    dense
                    hide-details="auto"
                    class="custom-input"
                    :rules="[required('Vehicle make')]"
                  />
                </div>
                <div class="input-group">
                  <label class="input-label">Vehicle model <span class="req">*</span></label>
                  <v-autocomplete
                    v-model="carModel"
                    :items="carModelItems"
                    :loading="modelLoading"
                    :search-input.sync="carModelSearch"
                    :disabled="!carMake"
                    placeholder="Select model"
                    outlined
                    dense
                    hide-details="auto"
                    class="custom-input"
                    :rules="[required('Vehicle model')]"
                  />
                </div>
              </div>
              <div class="form-row">
                <div class="input-group">
                  <label class="input-label">Year of manufacture <span class="req">*</span></label>
                  <v-autocomplete
                    v-model="year"
                    :items="yearList"
                    outlined
                    dense
                    hide-details="auto"
                    class="custom-input"
                    :rules="[required('Year')]"
                  />
                </div>
                <div class="input-group">
                  <label class="input-label">Vehicle value (KES) <span class="req">*</span></label>
                  <v-text-field
                    v-model="formattedValueInput"
                    placeholder="e.g. 1,500,000"
                    outlined
                    dense
                    hide-details="auto"
                    class="custom-input"
                    :rules="[required('Vehicle value'), minAmount('Vehicle value', 0)]"
                  />
                  <span class="value-hint">Required for comprehensive (min. 500,000)</span>
                </div>
              </div>
            </div>

            <p v-if="quoting" class="quote-status-hint tw-mt-3">
              <v-progress-circular indeterminate size="14" width="2" color="#00AEEF" class="tw-mr-2" />
              Updating live premium…
            </p>
          </v-form>
        </div>

        <div v-if="quoteReady" class="section-card tw-mt-4">
          <div class="section-header">
            <span class="section-number">2</span>
            <h3>Choose cover</h3>
          </div>

          <div class="cover-cards">
            <button
              v-for="opt in availableCovers"
              :key="opt.covertypeId"
              type="button"
              :class="['cover-card', selectedCovertypeId === opt.covertypeId ? 'cover-card--active' : '']"
              @click="selectCover(opt)"
            >
              <p class="cover-card-title">{{ opt.label }}</p>
              <p class="cover-card-desc">{{ opt.description }}</p>
              <p class="cover-card-premium">KES {{ formatMoney(opt.premium) }}</p>
              <p v-if="opt.rateApplied != null && opt.covertypeId === 101" class="cover-card-rate">{{ formatRate(opt.rateApplied) }}% of sum insured</p>
            </button>
          </div>

          <p v-if="!compRisk && carValueNum < minCompValue" class="cover-note">
            Comprehensive is available from KES {{ formatMoney(minCompValue) }} vehicle value.
          </p>
        </div>

        <div v-if="quoteReady && isComprehensiveSelected" class="section-card tw-mt-4">
          <div class="section-header">
            <span class="section-number">3</span>
            <h3>Additional benefits</h3>
          </div>
          <p class="step-hint">Windscreen and infotainment come free with your comprehensive cover. Tick any paid extras you want.</p>
          <benefit-limit-selector
            :benefits="chargeableBenefits"
            :selected-addons.sync="addonsDynamic"
            :custom-limits.sync="benefitCustomLimits"
            :loading="benefitsLoading"
            :updating="benefitUpdating || quoting"
            :premium-lookup="benefitPremiumMap"
            :car-value="carValueNum"
            :basic-premium="basicPremium"
            @change="handleBenefitToggle"
          />
        </div>

        <div v-if="quoteReady" class="tw-mt-4">
          <button
            type="button"
            class="btn-primary"
            :disabled="!selectedRiskId || benefitUpdating"
            @click="goToApply"
          >
            {{ proceedLabel }}
            <v-icon small color="white" class="tw-ml-1">mdi-arrow-right</v-icon>
          </button>
        </div>
      </div>

      <aside class="quote-sidebar">
        <motor-quote-sidebar
          :cover-label="sidebarCoverLabel"
          :vehicle-line="sidebarVehicle"
          :premium="sidebarPremium"
          :rate-applied="sidebarRate"
          :covertype-id="selectedCovertypeId"
          :quote-id="quoteId"
          :quote="exportQuote"
          channel="customer"
        />
      </aside>
    </div>
  </div>
</template>

<script>
import MotorQuoteSidebar from '@/components/motor/MotorQuoteSidebar';
import BenefitLimitSelector from '@/components/agent/BenefitLimitSelector';
import CustomerAgentPicker from '@/components/shared/CustomerAgentPicker';
import {
  MOTOR_COVER_OPTIONS,
  MOTOR_MIN_COMP_VALUE,
  MOTOR_COVER_COMPREHENSIVE,
} from '@/constants/motorPrivate';
import {
  createMotorQuote,
  findRiskByCovertypeId,
  getRiskPremium,
  getVehicleMakes,
  getVehicleModels,
} from '@/services/customerMotorApi';
import { referralAgencyNumericId } from '@/services/referralApi';
import { saveMotorPendingQuote } from '@/utils/saleCache';
import { getMotorChannelConfig, getCognitoIdentityProfile } from '@/utils/motorChannel';
import { buildMotorQuoteExportPayload } from '@/utils/motorQuoteDocument';
import {
  GUEST_SESSION_KEY,
  GUEST_QUOTE_ROUTES,
  guestBuyRouteForProduct,
  redirectGuestToBuy,
} from '@/utils/guestQuote';
import {
  applyBenefitSyncToQuote,
  applyLocalBenefitsToQuote,
  buildBenefitPremiumMap,
  computeUntaxedBasicPremium,
  fetchMotorChargeableBenefits,
  restoreBenefitSelectionFromRisk,
  syncMotorQuoteBenefits,
} from '@/utils/motorBenefits';
import validations from '@/utils/validations';
import { mapGetters } from 'vuex';

export default {
  name: 'MotorStartPage',
  components: { MotorQuoteSidebar, BenefitLimitSelector, CustomerAgentPicker },
  props: {
    guest: { type: Boolean, default: false },
  },
  data() {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= 2000; y -= 1) years.push(String(y));
    return {
      motorConfig: getMotorChannelConfig(),
      minCompValue: MOTOR_MIN_COMP_VALUE,
      ...validations,
      formValid: false,
      quoting: false,
      quoteReady: false,
      quote: null,
      quoteId: null,
      carMakes: [],
      carMake: null,
      carMakeSearch: null,
      carMakeObj: null,
      carMakeItems: [],
      carLoading: false,
      carModels: [],
      carModel: null,
      carModelSearch: null,
      carModelItems: [],
      modelLoading: false,
      year: String(currentYear),
      yearList: years,
      carValue: '',
      selectedCovertypeId: null,
      selectedRiskId: null,
      quoteTimer: null,
      chargeableBenefits: [],
      addonsDynamic: [],
      benefitCustomLimits: {},
      benefitsLoading: false,
      benefitUpdating: false,
      benefitDetails: [],
      basicPremium: 0,
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser']),
    ...mapGetters('referral', ['referralAgent']),
    productsRoute() {
      return this.guest ? GUEST_QUOTE_ROUTES.products : this.motorConfig.routes.products;
    },
    pageBreadcrumb() {
      return this.guest ? 'Get quote / Motor' : this.motorConfig.labels.startBreadcrumb;
    },
    pageTitle() {
      return this.guest ? 'Motor insurance — Get a quote' : this.motorConfig.labels.startTitle;
    },
    proceedLabel() {
      return this.guest ? 'Proceed to buy' : 'Continue to application';
    },
    sessionEmail() {
      return this.authenticatedUser?.email || (this.guest ? GUEST_SESSION_KEY : '');
    },
    referralAgentId() {
      return referralAgencyNumericId(this.referralAgent);
    },
    carValueNum() {
      return parseInt(this.carValue, 10) || 0;
    },
    formattedValueInput: {
      get() {
        if (!this.carValue) return '';
        return new Intl.NumberFormat('en-KE').format(this.carValue);
      },
      set(val) {
        let raw = String(val).replace(/\D/g, '');
        if (raw.length > 8) raw = raw.slice(0, 8);
        const num = parseInt(raw, 10);
        this.carValue = num > 50000000 ? 50000000 : (num || '');
      },
    },
    compRisk() {
      return this.quote ? findRiskByCovertypeId(this.quote, 101) : null;
    },
    tpoRisk() {
      return this.quote ? findRiskByCovertypeId(this.quote, 106) : null;
    },
    availableCovers() {
      if (!this.quote) return [];
      return MOTOR_COVER_OPTIONS.map((opt) => {
        const risk = findRiskByCovertypeId(this.quote, opt.covertypeId);
        if (!risk) return null;
        if (opt.covertypeId === 101 && this.carValueNum < MOTOR_MIN_COMP_VALUE) return null;
        return {
          ...opt,
          risk,
          riskId: risk.riskId,
          premium: getRiskPremium(risk),
          rateApplied: risk.rateApplied,
        };
      }).filter(Boolean);
    },
    sidebarCoverLabel() {
      const o = this.availableCovers.find((c) => c.covertypeId === this.selectedCovertypeId);
      return o?.label || '';
    },
    sidebarVehicle() {
      if (!this.carMake) return '';
      return `${this.carMake} ${this.carModel || ''} · ${this.year}`.trim();
    },
    sidebarPremium() {
      const o = this.availableCovers.find((c) => c.covertypeId === this.selectedCovertypeId);
      return o?.premium || 0;
    },
    sidebarRate() {
      const o = this.availableCovers.find((c) => c.covertypeId === this.selectedCovertypeId);
      return o?.rateApplied ?? null;
    },
    isComprehensiveSelected() {
      return Number(this.selectedCovertypeId) === MOTOR_COVER_COMPREHENSIVE;
    },
    benefitPremiumMap() {
      return buildBenefitPremiumMap(this.benefitDetails);
    },
    exportQuote() {
      if (!this.quoteId || !this.quote || !this.selectedCovertypeId) return {};
      const risk = findRiskByCovertypeId(this.quote, this.selectedCovertypeId);
      const profile = getCognitoIdentityProfile(this.authenticatedUser);
      return buildMotorQuoteExportPayload({
        quote: {
          ...this.quote,
          benefitDetails: this.isComprehensiveSelected ? this.benefitDetails : [],
        },
        activeRisk: risk,
        coverLabel: this.sidebarCoverLabel,
        vehicleLine: this.sidebarVehicle,
        premium: this.sidebarPremium,
        rateApplied: this.sidebarRate,
        clientName: profile?.fullName || '',
        clientEmail: profile?.email || '',
      });
    },
  },
  watch: {
    carMakeSearch(val) {
      if (val) this.filterMakes(val);
    },
    async carMake(val) {
      if (!val) return;
      this.carMakeObj = this.carMakes.find((x) => x.makeDetails === val);
      this.carModel = null;
      if (this.carMakeObj?.vmkcode) {
        await this.loadModels(this.carMakeObj.vmkcode);
      }
      this.debounceFetchQuote();
    },
    carModelSearch(val) {
      if (val) this.filterModels(val);
    },
    carModel() { this.debounceFetchQuote(); },
    year() { this.debounceFetchQuote(); },
    carValue() { this.debounceFetchQuote(); },
    selectedCovertypeId(val) {
      if (Number(val) === MOTOR_COVER_COMPREHENSIVE && this.quoteReady) {
        this.loadChargeableBenefits();
      }
    },
  },
  beforeDestroy() {
    if (this.quoteTimer) clearTimeout(this.quoteTimer);
  },
  async mounted() {
    await this.$store.dispatch('referral/ensureReferralValidated', this.$route.query);
    this.loadMakes();
    const profile = getCognitoIdentityProfile(this.authenticatedUser);
    if (profile?.email) {
      /* prefill not required on start — quote create sends cognito fields */
    }
  },
  methods: {
    formatMoney(n) {
      return Number(n || 0).toLocaleString('en-KE');
    },
    formatRate(r) {
      const n = Number(r);
      return Number.isInteger(n) ? n : n.toFixed(1);
    },
    async loadMakes() {
      this.carLoading = true;
      const res = await getVehicleMakes();
      this.carMakes = res.success ? res.data : [];
      this.carMakeItems = this.carMakes.map((m) => m.makeDetails);
      this.carLoading = false;
    },
    async loadModels(vmkCode) {
      this.modelLoading = true;
      const res = await getVehicleModels(vmkCode);
      this.carModels = res.success ? res.data : [];
      this.carModelItems = this.carModels.map((m) => m.modelname);
      this.modelLoading = false;
    },
    filterMakes(v) {
      this.carMakeItems = this.carMakes
        .map((m) => m.makeDetails)
        .filter((e) => (e || '').toUpperCase().includes((v || '').toUpperCase()));
    },
    filterModels(v) {
      this.carModelItems = this.carModels
        .map((m) => m.modelname)
        .filter((e) => (e || '').toUpperCase().includes((v || '').toUpperCase()));
    },
    debounceFetchQuote() {
      if (this.quoteTimer) clearTimeout(this.quoteTimer);
      this.quoteTimer = setTimeout(() => this.fetchQuote(), 600);
    },
    async fetchQuote() {
      if (!this.formValid || this.quoting) return;
      this.quoting = true;
      const profile = getCognitoIdentityProfile(this.authenticatedUser);
      const res = await createMotorQuote({
        make: this.carMake,
        model: this.carModel,
        yearOfManufacture: this.year,
        valueOfCar: this.carValueNum,
        email: profile?.email,
        fullName: profile?.fullName,
        kraPinNumber: profile?.kraPin,
        agentId: this.referralAgentId,
      });
      this.quoting = false;
      if (!res.success) {
        this.$toast.error(res.message || 'Could not create quote');
        return;
      }
      this.addonsDynamic = [];
      this.benefitCustomLimits = {};
      this.benefitDetails = [];
      this.quote = res.data?.quote || res.data;
      this.quoteId = this.quote?.quoteId;
      this.quoteReady = true;
      this.refreshBasicPremium();
      this.restoreBenefitsFromQuote();
      await this.loadChargeableBenefits();
      const stillValid = this.availableCovers.find((c) => c.covertypeId === this.selectedCovertypeId);
      const pick = stillValid || this.availableCovers[0];
      if (pick) this.selectCover(pick);
    },
    refreshBasicPremium() {
      const risk = this.compRisk;
      this.basicPremium = computeUntaxedBasicPremium(getRiskPremium(risk));
    },
    restoreBenefitsFromQuote() {
      const risk = this.compRisk;
      if (!risk?.additionalBenefits?.length) return;
      const restored = restoreBenefitSelectionFromRisk(risk.additionalBenefits);
      this.addonsDynamic = restored.addons;
      this.benefitCustomLimits = restored.limits;
    },
    async loadChargeableBenefits() {
      if (!this.compRisk || this.benefitsLoading) return;
      this.benefitsLoading = true;
      const res = await fetchMotorChargeableBenefits(MOTOR_COVER_COMPREHENSIVE);
      this.chargeableBenefits = res.success ? res.data : [];
      if (!res.success && res.message) {
        this.$toast.error(res.message);
      }
      this.benefitsLoading = false;
    },
    async handleBenefitToggle({ skipApiSync } = {}) {
      if (!this.quote || !this.compRisk) return;

      if (skipApiSync) {
        this.quote = applyLocalBenefitsToQuote(
          this.quote,
          this.compRisk.riskId,
          this.chargeableBenefits,
          this.addonsDynamic,
          this.benefitCustomLimits,
        );
        return;
      }

      this.benefitUpdating = true;
      try {
        const res = await syncMotorQuoteBenefits(
          this.compRisk.riskId,
          this.chargeableBenefits,
          this.addonsDynamic,
          this.benefitCustomLimits,
        );
        if (!res.success) {
          this.$toast.error(res.message || 'Failed to update benefits');
          return;
        }
        this.benefitDetails = res.data?.benefits || [];
        this.quote = applyBenefitSyncToQuote(
          this.quote,
          this.compRisk.riskId,
          res.data,
          this.chargeableBenefits,
          this.addonsDynamic,
          this.benefitCustomLimits,
        );
        this.refreshBasicPremium();
      } finally {
        this.benefitUpdating = false;
      }
    },
    selectCover(opt) {
      this.selectedCovertypeId = opt.covertypeId;
      this.selectedRiskId = opt.riskId;
    },
    goToApply() {
      if (!this.quoteId || !this.selectedRiskId) return;
      if (this.isComprehensiveSelected && this.addonsDynamic.length && this.compRisk) {
        this.quote = applyLocalBenefitsToQuote(
          this.quote,
          this.compRisk.riskId,
          this.chargeableBenefits,
          this.addonsDynamic,
          this.benefitCustomLimits,
        );
      }
      const email = this.sessionEmail;
      saveMotorPendingQuote(email, {
        quoteId: this.quoteId,
        riskId: this.selectedRiskId,
        covertypeId: this.selectedCovertypeId,
        quote: this.quote,
        addonsDynamic: this.addonsDynamic,
        benefitCustomLimits: this.benefitCustomLimits,
      });
      const buyRoute = guestBuyRouteForProduct('motor', {
        quoteId: this.quoteId,
        riskId: this.selectedRiskId,
      });
      if (this.guest) {
        redirectGuestToBuy(this.$router, buyRoute);
        return;
      }
      this.$router.push({
        name: this.motorConfig.routes.apply,
        query: { quoteId: this.quoteId, riskId: this.selectedRiskId },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.motor-page { max-width: 1200px; margin: 0 auto; padding: 20px 28px 32px; }
.motor-header { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 16px; }
.back-btn {
  width: 36px; height: 36px; border-radius: 50%; border: 1px solid #e2e8f0;
  background: white; display: flex; align-items: center; justify-content: center; cursor: pointer;
}
.quote-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 24px;
  align-items: start;
}
.quote-status-hint {
  display: flex; align-items: center; font-size: 0.8rem; color: #64748b; margin: 0;
}
@media (max-width: 720px) { .quote-layout { grid-template-columns: 1fr; } }
.cover-cards { display: flex; flex-direction: column; gap: 10px; }
.cover-card {
  text-align: left; padding: 14px 16px; border: 1.5px solid #e8edf2; border-radius: 12px;
  background: #f8fafc; cursor: pointer; transition: all 0.15s;
}
.cover-card--active {
  border-color: #1e4276; background: #f0f4fa; box-shadow: 0 0 0 1px #1e4276;
}
.cover-card-title { margin: 0 0 4px; font-weight: 700; color: #1e4276; font-size: 0.95rem; }
.cover-card-desc { margin: 0 0 8px; font-size: 0.78rem; color: #64748b; }
.cover-card-premium { margin: 0; font-size: 1.1rem; font-weight: 800; color: #00aeef; }
.cover-card-rate { margin: 4px 0 0; font-size: 0.72rem; color: #64748b; }
.cover-note { font-size: 0.78rem; color: #64748b; margin-top: 10px; }
.quote-sidebar { position: sticky; top: 20px; }
.value-hint { display: block; font-size: 0.72rem; color: #64748b; margin-top: 4px; }

@media (max-width: 960px) {
  .motor-page { padding: 16px 16px 28px; }
  .quote-layout { grid-template-columns: 1fr; gap: 16px; }
  .quote-sidebar { position: static; }
}

@media (max-width: 600px) {
  .motor-page { padding: 12px 12px 88px; }
  .motor-header {
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
  }
  .title { font-size: 1.15rem; line-height: 1.3; }
  .breadcrumb { font-size: 0.7rem; }
  .btn-primary {
    width: 100%;
    justify-content: center;
    min-height: 46px;
  }
}
</style>

<style lang="scss">
@import '@/styles/motor-customer.scss';
</style>
